/**
 * Engine-neutral identity contract.
 *
 * Supabase is the current implementation; per the 2026-06-06 identity decision
 * this is the single swappable seam — DO NOT call supabase.auth.* outside this
 * module. UI components import ONLY this contract, never the Supabase client.
 *
 * Every operation returns a normalized envelope so the UI never sees a raw
 * provider (Supabase) error object — a future engine swap stays contained here:
 *     success → { ok: true,  data: <normalized> }
 *     failure → { ok: false, error: { code: string, message: string } }
 *
 * Operations are phrased as session/credential INTENTIONS, not provider calls.
 *
 * Backings today:
 *   - client-direct credential ops → Supabase Auth (updateUser / reauth / reset / signOut)
 *   - seam reads (whoAmI / hasPassword) → Library shared-core over the `/api/library`
 *     proxy, authenticated with the CURRENT USER's session token.
 *   - user-management ops (B-2) → CAPTURE-BACKEND proxy `/api/capture/admin/users/*`
 *     with the user token + x-tenant-id. The contract sends ONLY target params; the
 *     capture backend derives the ACTOR from the verified session (never the client).
 *     The contract never calls library directly for these.
 */

import { supabase } from '../auth/supabaseClient';
import { getActiveTenantId } from '../services/activeTenant';

// Established capture-ui → Library base (mirrors services/api.js libraryApi).
// `/api/library/<x>` is proxied to Library `/api/<x>`.
const LIBRARY_BASE = process.env.REACT_APP_LIBRARY_BASE_PATH || '/api/library';

// ── normalized envelope helpers ──────────────────────────────────────────
function ok(data) {
  return { ok: true, data };
}
function fail(code, message) {
  return { ok: false, error: { code: String(code), message: String(message) } };
}

/**
 * Convert ANY provider/transport error into the normalized failure envelope so
 * the UI never touches a raw Supabase error object.
 */
function normalizeError(err, fallbackCode) {
  const code = (err && (err.code || err.name)) || fallbackCode || 'identity_error';
  const message = (err && err.message) ? String(err.message) : 'Unexpected error';
  return fail(code, message);
}

// ── internal seam plumbing ───────────────────────────────────────────────
async function currentAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token ?? null;
}

/** Authenticated GET to a Library shared-core seam endpoint with the USER token. */
async function seamGet(path) {
  const token = await currentAccessToken();
  if (!token) return fail('not_authenticated', 'No active session');
  let res;
  try {
    res = await fetch(`${LIBRARY_BASE}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    return normalizeError(e, 'network_error');
  }
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg = (body && body.error) ? body.error : `Request failed (${res.status})`;
    return fail(`http_${res.status}`, msg);
  }
  return ok(body);
}

// Base for the capture-backend user-management proxy (B-2). The capture axios path
// is /api/capture/*; this module uses fetch directly (it already owns the token).
const CAPTURE_USERS_BASE = '/api/capture/admin/users';

/**
 * Authenticated POST to a capture-backend user-mgmt proxy route, with the USER
 * token + x-tenant-id (the active tenant). The body carries ONLY target params —
 * the ACTOR is set server-side from req.auth.userId, never here. The capture
 * backend replies in its `{ ok, ...data } | { ok:false, error, details }` envelope;
 * we re-wrap into the contract envelope (stripping the backend's `ok`).
 */
async function captureUsersPost(path, targetParams) {
  const token = await currentAccessToken();
  if (!token) return fail('not_authenticated', 'No active session');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const tenantId = getActiveTenantId();
  if (tenantId) headers['x-tenant-id'] = tenantId;

  let res;
  try {
    res = await fetch(`${CAPTURE_USERS_BASE}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(targetParams || {}),
    });
  } catch (e) {
    return normalizeError(e, 'network_error');
  }
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg = body && body.error ? body.error : `Request failed (${res.status})`;
    return fail(`http_${res.status}`, msg);
  }
  // Strip the backend's `ok` flag; expose the rest as the contract's data.
  const data = body && typeof body === 'object' ? { ...body } : {};
  delete data.ok;
  return ok(data);
}

// ─────────────────────────────────────────────────────────────────────────
// IDENTITY / SELF-SERVICE (wired in B-1)
// ─────────────────────────────────────────────────────────────────────────

/** Current user + tenant + role (seam read: Library bootstrap). */
export async function whoAmI() {
  const r = await seamGet('/bootstrap');
  if (!r.ok) return r;
  const b = r.data || {};
  return ok({
    userId: (b.user && b.user.id) || null,
    email: (b.user && b.user.email) || null,
    tenantId: b.tenantId || null,
    role: b.role || null,
    allowedAuthProviders: (b.tenant && b.tenant.allowedAuthProviders) || [],
  });
}

/** Whether the current user has a password credential set (seam read). */
export async function hasPassword() {
  const r = await seamGet('/me/has-password');
  if (!r.ok) return r;
  return ok({ hasPassword: Boolean(r.data && r.data.hasPassword) });
}

/**
 * The set of tenants the CURRENT user belongs to + the shared current selection
 * (self-service seam read over Library GET /api/tenants, user token). Used by the
 * tenant switcher's LIST. Normalizes to { tenants: [{id,name,role}], currentTenantId }.
 * Fail-soft: callers degrade to current-tenant-only on { ok:false } (never throw).
 */
export async function listMyTenants() {
  const r = await seamGet('/tenants');
  if (!r.ok) return r;
  const b = r.data || {};
  const tenants = Array.isArray(b.tenants)
    ? b.tenants.map((t) => ({ id: t.id, name: t.name ?? null, role: t.role ?? null }))
    : [];
  return ok({ tenants, currentTenantId: b.currentTenantId ?? null });
}

/** Set the initial password for an account that has none (client-direct). */
export async function setInitialPassword(newPassword) {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return normalizeError(error, 'set_password_failed');
    return ok({ updated: true });
  } catch (e) {
    return normalizeError(e, 'set_password_failed');
  }
}

/** Change an existing password: verify current credential, then update. */
export async function changePassword(currentPassword, newPassword) {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const email = sessionData && sessionData.session && sessionData.session.user
      ? sessionData.session.user.email
      : null;
    if (!email) return fail('not_authenticated', 'No active session');

    // Re-authenticate to verify the current credential before changing it.
    const { error: reauthError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });
    if (reauthError) return fail('invalid_current_password', 'Current password is incorrect');

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return normalizeError(error, 'change_password_failed');
    return ok({ updated: true });
  } catch (e) {
    return normalizeError(e, 'change_password_failed');
  }
}

/** Request a password-reset link be emailed (client-direct). */
export async function requestPasswordReset(email) {
  try {
    const redirectTo = (typeof window !== 'undefined' && window.location)
      ? `${window.location.origin}/login`
      : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(
      email,
      redirectTo ? { redirectTo } : undefined,
    );
    if (error) return normalizeError(error, 'reset_request_failed');
    return ok({ requested: true });
  } catch (e) {
    return normalizeError(e, 'reset_request_failed');
  }
}

/** Complete a reset after following the email link (recovery session active). */
export async function completePasswordReset(newPassword) {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return normalizeError(error, 'reset_complete_failed');
    return ok({ updated: true });
  } catch (e) {
    return normalizeError(e, 'reset_complete_failed');
  }
}

/** End the current session (client-direct). */
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) return normalizeError(error, 'sign_out_failed');
    return ok({ signedOut: true });
  } catch (e) {
    return normalizeError(e, 'sign_out_failed');
  }
}

// ─────────────────────────────────────────────────────────────────────────
// USER MANAGEMENT (B-2 — wired to the capture-backend proxy /api/capture/admin/users/*).
// The contract sends ONLY target params; the capture backend sets the actor from
// the verified session. The real admin enforcement is library's actor-admin gate.
// ─────────────────────────────────────────────────────────────────────────

/** List tenant users + roles + dept grants + option lists (catalog). */
export async function listUsers() {
  return captureUsersPost('/list', {});
}

/**
 * Invite a user to the current tenant.
 *
 * `product` selects the accept-link origin server-side (see backend
 * createInvite). This is the Capture contract, so invites default to
 * product:'capture' → the accept link points at capture.steliad.com and the
 * invited user completes auth on — and lands in — Capture. Passed explicitly;
 * the backend never sniffs the request Origin.
 */
export async function inviteUser({ email, role, expiresInDays, product = 'capture' } = {}) {
  return captureUsersPost('/invite', { email, role, expiresInDays, product });
}

/** List pending (unaccepted, unrevoked, unexpired) invites for the tenant. */
export async function listInvites() {
  return captureUsersPost('/invite/list', {});
}

/** Revoke a pending invite. */
export async function revokeInvite(inviteId) {
  return captureUsersPost('/invite/revoke', { inviteId });
}

/** Change a user's tenant role. */
export async function changeUserRole(userId, role) {
  return captureUsersPost('/role', { userId, role });
}

/** Set (PUT-semantics) a user's department permission grants for one department. */
export async function grantDepartment(userId, departmentKey, permissions) {
  return captureUsersPost('/department/grant', { userId, departmentKey, permissions });
}

/** Revoke a user's department grants (one permission, or all if omitted). */
export async function revokeDepartment(userId, departmentKey, permission) {
  return captureUsersPost('/department/revoke', { userId, departmentKey, permission });
}

/** Resolved effective access summary for a user. */
export async function getEffectiveAccess(userId) {
  return captureUsersPost('/effective-access', { userId });
}
